import { ChangeDetectorRef, Injectable } from '@angular/core';
import { Categories, Filter, FilterCategory, FilterProperty } from '../classes/filter';
import { FilterActionCreatorService } from './filter-action-creator.service';
import { HttpInterceptorService } from '../../core/services/http-interceptor.service';
import { Subscription } from 'rxjs/Subscription';
import { Observable } from 'rxjs/Observable';
import { of, throwError } from 'rxjs';
import { catchError, filter, flatMap, map, share, take, tap, withLatestFrom } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { IFiltersState } from '../store/filter.reducer';
import { DateHandler } from '../../shared/utils/DateHandler';
import { FilterUtils } from '../utils/filter-utils';
import { BundleCall } from '@ispm-client/modules/aggregator/services';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { cloneDeep, get, has } from 'lodash';
import { WebConfigService } from '@ispm/layout/services/webConfig.service';
import { SearchService } from '@ispm/dynamic-form/services/search.service';
import { ConfigurationStoreHandler } from '../../configuration/services/configuration-store-handler.service';
import { NOTIFICATION_MESSAGE } from '@ispm-client/modules/aggregator/services/consts';
import { IWorklistColumnItem } from '../../worklist/interfaces/worklist-action.interface';
import {
  FILTER_APPLIED,
  FILTER_CURRENT,
  FILTER_DATE_COMING_WEEK,
  FILTER_DATE_LAST_WEEK,
  FILTER_DATE_NEXT_WEEK,
  FILTER_DATE_NOW,
  FILTER_QUERY_MANDATORY,
} from './filter.service.const';
import moment from 'moment';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { EXPORT_DATA_STORAGE_KEY } from "@ispm/shared/services/ui-customization.service";
import { BehaviorSubject } from "rxjs/BehaviorSubject";

@Injectable()
export class FilterService {
  public static baseUrl = '/filter';
  public static FilterPanelName = 'FILTERS_MODAL';

  private datePipe: DatePipe = new DatePipe('en-US');
  private _filterByOrgContext: boolean = false;  // this property is added to hold filterByOrgContext which is required for savedFilter to work
  private _currentWorklistColumns: IWorklistColumnItem[] = [];
  private _isWorklistFilter: boolean = false;
  private _lastFilterPanelValues: {selectedFilterId: string, selectedFilterCategories: any, formData: any}; //Holds the last filter panel values (the values in the panel and not the values of the current filter) before he panel was closed
  private _categoriesObservable: Observable<any>;
  private readonly _selectedRows$: BehaviorSubject<any[]>;

  constructor(
    private authService: AuthenticationService,
    private notificationMessageService: NotificationMessageService,
    private notificationMessage: NOTIFICATION_MESSAGE,
    private _http: HttpInterceptorService,
    private webConfigService: WebConfigService,
    private translateService: TranslateService,
    private filterAC: FilterActionCreatorService,
    private _bundle: BundleCall,
    private _formHandler: FormHandlerService,
    private _configurationStoreHandler: ConfigurationStoreHandler,
    private _searchService: SearchService
  ) {
    this.restoreWorklistColumns();
    this._selectedRows$ = new BehaviorSubject([]);
  }

  private restoreWorklistColumns(): void {
    const restoredWorklistColumnsJSON: string = sessionStorage.getItem('worklistColumns');
    if (restoredWorklistColumnsJSON) {
      try {
        this._currentWorklistColumns = JSON.parse(restoredWorklistColumnsJSON);
      } catch (e) { }
    }
  }

  set currentWorklistColumns(columns: IWorklistColumnItem[]) {
    this._currentWorklistColumns = columns;
    sessionStorage.setItem('worklistColumns', JSON.stringify(this._currentWorklistColumns));
  }

  get currentWorklistColumns(): IWorklistColumnItem[] {
    return this._currentWorklistColumns;
  }

  set isWorklistFilter(val: boolean) {
    this._isWorklistFilter = val;
  }

  get lastFilterPanelValues(): {selectedFilterId: string, selectedFilterCategories: any, formData: any} {
    return this._lastFilterPanelValues;
  }

  set lastFilterPanelValues(val: {selectedFilterId: string, selectedFilterCategories: any, formData: any}) {
    this._lastFilterPanelValues = val;
  }

  public setSelectedFilterItem(selectedFilter): any {
    return {
      label: selectedFilter.name,
      id: selectedFilter.id,
      undeletable: selectedFilter.hasOwnProperty('undeletable') ? selectedFilter.undeletable : false,
      disabled: selectedFilter.id === '0'
    };
  }

  public setFilterType(filterType: string, filterSubtype: string): void {
    this.filterAC.setFilterType(filterType, filterSubtype);
  }

  public setCurrentSelectedTab(currentSelectedTab: string): void {
    this.filterAC.setCurrentSelectedTab(currentSelectedTab);
  }

  public getCurrentTypeAndSubtypeFromStore(): Observable<{ type: string, subtype: string } | { }> {
    return this.filterAC.getState([FILTER_CURRENT])
      .take(1)
      .map((currentTypeAndSubtype: string) => {
        if (currentTypeAndSubtype === '') {
          currentTypeAndSubtype = JSON.parse(localStorage.getItem('aberrationType'));
          const type = currentTypeAndSubtype.split('_');
          return { type: type[0], subtype: type[1] };
        } else {
          localStorage.setItem('aberrationType', JSON.stringify(currentTypeAndSubtype));
          const types = currentTypeAndSubtype.split('_');
          return { type: types[0], subtype: types[1]};
        }
      });
  }

  public getCurrentSelectedFilterFromStore(latest: boolean = false): Observable<{ filterTypeAndSubtype: string, filter: Filter, categories: any | null }> {
    if (latest) {
      return this.filterAC.getState([FILTER_CURRENT]).switchMap((current: string) => {
        return this.filterAC.getState(['currentSelected']).map(data => {
          if (data.has(current)) {
            return data.get(current);
          } else {
            return { };
          }
        });
      });
    }

    return this.filterAC.getState([FILTER_CURRENT]).pipe(
      // take(1),
      flatMap((current: string) => {
        return this.filterAC.getCurrentSelectedFilter(current);
      })
    );
  }

  public getAppliedFilterPropertiesFromStore(): Observable<any[] | null> {
    return this.filterAC.getAppliedFilterProperties();
  }

  /** Called when switching modalities in aberrations module. */
  public getDefaultFilter(filterType: string, filterSubtype: string): Observable<any> {
    let defaultFilter: any;

    const key = filterType + '_' + filterSubtype;

    return this.filterAC.getState([FILTER_APPLIED])
      .take(1)
      .map(data => {
        if (data.has(key)) {
          const filtersList = data.get(key);
          if (filtersList) {
            defaultFilter = Array.from(filtersList.values()).find((filter: Filter) => filter.default === true);
          }
        }
        return defaultFilter;
      });
  }

  public getCurrentDefaultCategories(): Observable<any> {
    let currentFilterTypeAndSubtype: string;
    let currentDefaultCategories: any = { };

    return this.filterAC.getState([FILTER_CURRENT])
      .take(1)
      .map((current: string) => {
        currentFilterTypeAndSubtype = current;
      })
      .flatMap(() => {
        return this.filterAC.getState(['defaultCategories'])
          .take(1);
      })
      .map((defaultCategories: any) => {
        if (defaultCategories
          && defaultCategories.has(currentFilterTypeAndSubtype)) {
          // Important: clone the extracted currentFilterTypeAndSubtype to avoid changing it outside the map
          currentDefaultCategories = cloneDeep(defaultCategories.get(currentFilterTypeAndSubtype));
        }
        return currentDefaultCategories;
      });

    // return currentDefaultCategories;
  }

  public getManagedFiltersFromStore(): Observable<Filter[]> {
    return this.filterAC.getState([FILTER_CURRENT])
      .take(1)
      .flatMap((current: string) => {
        return this.filterAC.getCurrentAppliedFilters(current).take(1);
      })
      .map((currentFilters) => {
        let orderedManagedFilters: any[] = [];
        if (currentFilters.key && currentFilters.data) {
          const managedFilters: any[] = Array.from(currentFilters.data.values())
            .filter((filter: Filter) => !filter.default && filter.id !== '0');
          if (managedFilters.length > 0) {
            orderedManagedFilters = this.orderManagedFiltersAscByCreateData(managedFilters);
          }
        }

        return orderedManagedFilters;
      });
  }

  public getCurrentSelectedFilterQueryFromStore(): Observable<any | null> {
    return this.filterAC.getState(['currentSelectedQuery']).pipe(
      withLatestFrom(this.filterAC.getState([FILTER_CURRENT])),
      filter((current: string) => !!current),
      flatMap((current: string) => {
        return this.filterAC.getCurrentSelectedFilterQuery(current[1]).take(1);
      })
    );
  }

  public getCurrentSelectedFilterConfigFromStore(): Observable<any | null> {
    return this.filterAC.getState(['currentSelectedConfig']).pipe(
      withLatestFrom(this.filterAC.getState([FILTER_CURRENT])),
      filter((current: string) => !!current),
      flatMap((current: string) => {
        return this.filterAC.getCurrentSelectedFilterConfiguration(current[1]).take(1);
      })
    );
  }

  public getCurrentSelectedFormDataFromStore(): Observable<any | null> {
    return this.filterAC.getState(['currentSelectedFormData']).pipe(
      withLatestFrom(this.filterAC.getState([FILTER_CURRENT])),
      filter((current: string) => !!current),
      flatMap((current: string) => {
        return this.filterAC.getCurrentSelectedFormData(current[1]).take(1);
      })
    );
  }

  public getSelectedFilterQueryString(filterType: string, filterSubtype: string): Observable<string> {
    let queryString = '';
    return this.filterAC.getState(['currentSelectedQuery'])
      .map((currentSelectedQueries) => {
        const filterTypeAndSubtype = filterType + '_' + filterSubtype;
        if (currentSelectedQueries.has(filterTypeAndSubtype)) {
          const selectedQueryObj = currentSelectedQueries.get(filterTypeAndSubtype);
          if (selectedQueryObj && Object.keys(selectedQueryObj)) {
            queryString = this.buildFilterString(selectedQueryObj);
          }
        }
        return queryString;
      });
  }

  public getState(): Observable<IFiltersState> {
    return this.filterAC.getState();
  }

  public storeState() {
    this.filterAC.storeState();
  }

  public getAllApplied(): Observable<any> {
    return this.filterAC.getState([FILTER_APPLIED]);
  }

  public restoreState() {
    this.filterAC.restoreState();
  }

  public areFiltersInCache(): boolean {
    return this.filterAC.areFiltersInCache();
  }

  /**
   * This method is called by the component embedding the filterPanel component,
   * before the toggle button of the filter panel is being clicked.
   */
  public loadAppliedFilter(filterType: any, filterSubtype: any, defaultFilterSubtype: any, additionalParams?: any): Observable<{ defaultFilter: any; queryObj: any }> {
    let filterQueryObject: any = { };
    let defaultFilter: any = { };

    return this.loadFiltersList(filterType, filterSubtype, defaultFilterSubtype, additionalParams).
    pipe(
        map(filters => {
          const defaultFilters = filters.filter(filter => filter.default === true);

          if (defaultFilters && defaultFilters.length > 0) {
            // defaultFilter = defaultFilters[0];
            defaultFilter = defaultFilters.find(reference => {
              return reference.default;
            });
            if (defaultFilter.hasOwnProperty(FILTER_QUERY_MANDATORY) && defaultFilter.queryMandatory !== '') {
              filterQueryObject = this.buildInitialFilterQuery(defaultFilter.query, defaultFilter.queryMandatory);
            } else {
              filterQueryObject = this.buildInitialFilterQuery(defaultFilter.query);
            }
            return { defaultFilter: defaultFilter, queryObj: filterQueryObject};
          }

          console.error('No Default Filter Found!!! Params: ', filterType, filterSubtype, defaultFilterSubtype, additionalParams);
        }),
        catchError(err => {
          // This error is thrown in case loading a test filter was started but the user exited the test before
          // the filter loading was completed. In this case the test filter will be shown in the worklist (instead of the worklist filter). Therefor this filter list should be ignored.
          return Observable.throwError(err.message);
        })
      );
  }

  public setCurrentSelectedQuery(filterType: string, filterSubtype: string, query: any, formData: any) {
    this.filterAC.setCurrentSelectedQuery(filterType, filterSubtype, query, formData);
  }

  public applyFilter(filterType: string, filterSubtype: string, categories: any, filterFormData: any) {
    if (sessionStorage.getItem(EXPORT_DATA_STORAGE_KEY)) {
      sessionStorage.removeItem(EXPORT_DATA_STORAGE_KEY);
      this.setSelectedRows([]);
    }
    const updatedFilterQuery = this.buildFilterQueryByCategories(categories);
    const filterTypeAndSubtype = FilterUtils.buildFilterTypes(filterType, [filterSubtype]);
    this.filterAC.getCurrentSelectedFilter(filterTypeAndSubtype)
      .subscribe((currentFilterAndCategories: { filterTypeAndSubtype: string, filter: Filter, categories: any }) => {
        currentFilterAndCategories = cloneDeep(currentFilterAndCategories);
        if (currentFilterAndCategories && currentFilterAndCategories.filter) {
          if (currentFilterAndCategories.filter.hasOwnProperty(FILTER_QUERY_MANDATORY)
            && currentFilterAndCategories.filter.queryMandatory !== '') {
            const queryMandatoryObj = this.buildInitialFilterQuery(currentFilterAndCategories.filter.queryMandatory);
            if (queryMandatoryObj) {
              Object.keys(queryMandatoryObj).forEach(queryMandatoryKey => {
                updatedFilterQuery[queryMandatoryKey] = queryMandatoryObj[queryMandatoryKey];
              });
            }
          }
        }

        this.setCurrentSelectedQuery(filterType, filterSubtype, updatedFilterQuery, filterFormData);

      });
  }

  public orderManagedFiltersAscByCreateData(managedFilters: Filter[]): Filter[] {
    let orderedManagedFilters: Filter[] = [];
    if (managedFilters.length > 0) {
      orderedManagedFilters = managedFilters.sort((a, b) => {
        if (a.meta && a.meta.lastUpdated && b.meta && b.meta.lastUpdated) {
          return DateHandler.getTime(new Date(a.meta.lastUpdated)) - DateHandler.getTime(new Date(b.meta.lastUpdated));
        }
      });
    }
    return orderedManagedFilters;
  }

  public buildInitialFilterQuery(filterQueryString?: string, filterQueryMandatoryString?: string): any {
    const filterQueryObj: any = { };

    let completeFilterQueryString: string;
    if (filterQueryString) {
      completeFilterQueryString = filterQueryString;
    }
    if (filterQueryMandatoryString) {
      completeFilterQueryString = completeFilterQueryString + '&' + filterQueryMandatoryString;
    }
    if (completeFilterQueryString && completeFilterQueryString !== '') {
      const filterProps: any[] = completeFilterQueryString.split('&');
      if (filterProps && filterProps.length > 0) {
        filterProps.forEach(prop => {
          const filterProperty: any[] = prop.split('=');
          if (filterProperty) {
            if (filterProperty.length === 2) {
              filterQueryObj[filterProperty[0]] = filterProperty[1];
            } else if (filterProperty.length > 2) {
              const propValueIndex = prop.indexOf('=');
              if (propValueIndex !== -1) {
                const propValue = prop.substr(propValueIndex + 1, prop.length);
                if (propValue.includes('>=') ||
                  propValue.includes('<=') ||
                  propValue.includes('=')) {
                  if (!propValue.includes('[')) {
                    filterQueryObj[filterProperty[0]] = '[' + propValue + ']';
                  }
                  else {
                    filterQueryObj[filterProperty[0]] = propValue;
                  }
                }
              }
            }
          }
        });
      }
    }

    return filterQueryObj;
  }

  private getWebConfiguration(): Observable<boolean> {
    return this.webConfigService.getWebConfiguration()
      .pipe(
        filter(orgWebConfig => !!orgWebConfig),
        map(orgWebAdmin => orgWebAdmin.filterByOrgContext)
      );
  }

  private buildContextForFiltersListRetrieval(initialContext: string, type: string, subType: any, userId: string = '', additionalParams?: any): string {
    let context: string = initialContext;
    const contextParams = [];

    contextParams.push(type);
    if (subType instanceof Array) {
      contextParams.concat(subType);
    } else {
      contextParams.push(subType);
    }

    context = context + contextParams.join(',');
    const testId = get(additionalParams, 'testId', undefined);
    if (!!testId) {
        context = `${context},${testId}`;
    }

    context = (!!userId && userId !== '') ? `${context},${userId}` : `${context},null`;
    return context;
  }

  public buildFilterQuery(categories: any): string {
    const filterQueryObj = this.buildFilterQueryByCategories(categories);
    const filterArray = Object.keys(filterQueryObj).map(key => {
      return key + '=' + filterQueryObj[key];
    });
    return filterArray.join('&');
  }

  public buildFilterQueryByCategories(categories: any): any {
    const filterQuery = { };
    Object.keys(categories).forEach(categoryKey => {
      const categoryFilterProperties = categories[categoryKey].filters;
      categoryFilterProperties.forEach((filterProperty) => {
        if (filterProperty.selectedValue) {
          const value = this.getFilterValueByProperty(filterProperty);
          if (value) {
            filterQuery[filterProperty.name] = value;
          }
        }
      });
    });
    return filterQuery;
  }

  private isFilterPropertyApplied(filterProperty: any): boolean {
    let isToCheck: boolean = false;
    if (filterProperty.selectedValue.value) {
      if (filterProperty.selectedValue.value instanceof Array && filterProperty.selectedValue.value.length > 0) {
        isToCheck = true;
      }
    }
    else if (filterProperty.selectedValue.from || filterProperty.selectedValue.to) {
      isToCheck = true;
    }
    return isToCheck;
  }

  public loadFiltersList(filterType: string, filterSubtype: string, defaultFilterSubtype: string, additionalParams?: any, onlyDefaultFilters: boolean = false): Observable<any> {
    const completeFiltersList: Filter[] = [];
    const userId = this.authService.user.id;
    const userOrgId = this.authService.user.organization;
    let contextWithUserId = '[';
    let contextWithNullUserId = '[';
    let definition;
    let definitionConfig;
    let isDefinitionForm: boolean = false;
    let isFilterByOrgContext: boolean = false;
    const isWorklistFilterOnEnter = this._isWorklistFilter;
    const formFilter: {
      isForm: boolean;
      categoryBinding: {
        [categoryId: string]: {
          formId: string;
          formVertical: boolean;
        };
      }
    } = {
      isForm: false,
      categoryBinding: { },
    };

    this.getWebConfiguration()
      .subscribe(webConfig => {
        if (webConfig) {
          isFilterByOrgContext = true;
          this._filterByOrgContext = true;
          contextWithUserId = `${contextWithUserId}${userOrgId},`;
          contextWithNullUserId = `${contextWithNullUserId}${userOrgId},`;
        }
      })
      .unsubscribe();

    const params = !!additionalParams ? additionalParams : [];
    contextWithUserId = this.buildContextForFiltersListRetrieval(contextWithUserId, filterType, filterSubtype, userId, params);

    if (!isFilterByOrgContext) {
      contextWithNullUserId = this.buildContextForFiltersListRetrieval(contextWithNullUserId, filterType, filterSubtype, '', params);
    }

    contextWithUserId = `${contextWithUserId}]`;
    contextWithNullUserId = `${contextWithNullUserId}]`;

    let filterExecutor = of([]);
    if (!onlyDefaultFilters) { // When working from Org admin, getFiltersList(contextWithUserId) always returns an empty array so no need to call it. of([]) enables this.
      filterExecutor = this.getFiltersList(contextWithUserId, get(params, 'applicationId'));
    }

    let defaultFilter: any;

    return forkJoin([filterExecutor
      .catch(err => {
        console.log(err.error);
        return Observable.of(err.error);
      })
      .pipe(
        map(managedFilters => {
          if (managedFilters && managedFilters.length > 0) {
            managedFilters.forEach(filterItem => filterItem.userFilter = true);
            completeFiltersList.push(...managedFilters);
          }
          return completeFiltersList;
        })),
        this.getFiltersList(contextWithNullUserId, get(params, 'applicationId'))
            .catch(err => {
              console.log(err.error);
              return Observable.of(err.error);
            })
            .map(defaultFilters => {
              if (defaultFilters && defaultFilters.length > 0) {
                defaultFilter = defaultFilters.find(reference => {
                  return reference.default;
                });
                completeFiltersList.push(...defaultFilters);
              }
              return defaultFilters;
            })
        ]).pipe(
      flatMap(forkJoinFilters => {
        const defaultFilters = forkJoinFilters[1];// Since index 1 will have second responbse from forkjoin which willmake parallel call
        let params: any = { };
        let filterDefinitionId: string;

        if (defaultFilters && defaultFilters.length > 0) {
          filterDefinitionId = defaultFilters[0].filterDefinitionId;
        }

        if (filterDefinitionId) {
          if (additionalParams) {
            additionalParams.filterDefinitionId = filterDefinitionId;
            params = additionalParams;
          } else {
            params.filterDefinitionId = filterDefinitionId;
          }
        } else {
          params = additionalParams;
        }

        // add filterDefinition related api call here
        if (!params) {
          return of([]);
        }

        return this.resolveFilterDefinitionId(params.filterDefinitionId).pipe(flatMap(data => {
          definition = data ? get(data, 'categories') : [];
          definitionConfig = get(data, 'config');

          if (!definition) {
            definition = []; // this is very much required
          }

          isDefinitionForm = definition.length > 0 && definition[0].filterType === 'filterForm';

          if (isDefinitionForm) {
            formFilter.isForm = true;
            const formCategory: any = { };
            const formConfigs = [];
            // let formConfigs: immutableMap<string,Observable<any>>=immutableMap.Map();
            for (let i = 0; i < definition.length; i++) {
              formFilter.categoryBinding[definition[i].id] = {
                formId: definition[i].formId,
                formVertical: typeof definition[i].formVertical === 'boolean' ? definition[i].formVertical : true,
              };
              formConfigs.push(this.fetchFormConfig(definition[i].formId).pipe(
                map(categoryCopy => {
                  formCategory[definition[i].id] = categoryCopy;
                  return formCategory;
                })));
            }
            return forkJoin(...formConfigs);
          } else {
            return this.getCategories(filterType, params)
              .catch(err => {
                console.log(err.error);
                return Observable.of(err.error);
              });
          }
        }));
      }),
      map(categoriesRes => {
        let filter: Filter;

        if (Array.isArray(categoriesRes) && categoriesRes.length > 0 && !has(categoriesRes[0], 'path')) {
          categoriesRes = categoriesRes[0];
        }

        const filters: Filter[] = new Array<Filter>();

        // delete all excluded fields from categories list, we not need it
        this.checkExcludedCategoryFields(definition, categoriesRes);

        let appliedFilterProperties: any[] = [];

        completeFiltersList.forEach((filterInList) => {
          if ((filterInList && filterInList.filterType !== 'filterForm') && !(isDefinitionForm)) {
            this.initFilterProperties(categoriesRes);
          }
          filter = filterInList;
          const filtersValueMap = this.buildFiltersValuesMap(filter);
          if (filtersValueMap) {
            filtersValueMap.forEach(filterValues => {
              // updated for aberrations, verify for curation.
              filter.queryString = this.buildFilterString(filterValues.selectedFilterValues);
            });
          }
          appliedFilterProperties = this.initAppliedFilters(categoriesRes, filtersValueMap, isDefinitionForm);
          filters.push(filter);
        });

        if (!onlyDefaultFilters) { // Do not add Manage filter to org admin
          let filterName = 'Manage filter set';
          if (defaultFilter && defaultFilter.hasOwnProperty('filterResourceTitle')) {
            filterName = 'Manage ' + defaultFilter.filterResourceTitle.toLowerCase();
          }

          const filterData: any = {
            name: filterName,
            id: '0',
            default: false
          };
          filter = filterData;
          filters.push(filterData);
        }

        let currentDefaultFilterQueryObj;
        if (defaultFilter && defaultFilter.hasOwnProperty('query')) {
          if (defaultFilter.hasOwnProperty(FILTER_QUERY_MANDATORY)
            && defaultFilter.queryMandatory !== '') {
            currentDefaultFilterQueryObj = this.buildInitialFilterQuery(defaultFilter.query, defaultFilter.queryMandatory);
          } else {
            currentDefaultFilterQueryObj = this.buildInitialFilterQuery(defaultFilter.query);
          }
        } else {
          currentDefaultFilterQueryObj = this.buildInitialFilterQuery();
        }

        let currentDefaultFilterFormData;
        if (defaultFilter && defaultFilter.hasOwnProperty('formData')) {
          currentDefaultFilterFormData = defaultFilter.formData;
        }

        // Handles the case of getting from the worklist into a test that uses filters (e.g. genomics test with aberration filters),
        // and while the test is shown and the test filters are loaded in the background, the user clicks on 'Worklist'. In this scenario, the test filters loading will be completed after the worklist is shown
        // and wrong filters will be shown in the worklist.
        // In order to fix this, if _isWorklistFilter was false when entering this function and is true at this stage, it means that the user clicks on Worklist
        // while test filters loading was started but not completed and test filters loading should be stopeed.
        if (this._isWorklistFilter && !isWorklistFilterOnEnter) {
          throw new Error('Cancelled');
        }

        this.filterAC.setCurrentFilters(filterType, filterSubtype, defaultFilterSubtype, filters, categoriesRes, currentDefaultFilterQueryObj, formFilter, definitionConfig, currentDefaultFilterFormData);
        const filterTypeAndSubtype = filterType + '_' + filterSubtype;
        this.filterAC.setAppliedFilterProperties(appliedFilterProperties, filterTypeAndSubtype);

        return filters;
      })
    );
  }

  // TODO: testId needs to be removed after integration with Eliran.
  public getFiltersList(context: string, applicationId?: string): Observable<Filter[]> {
    const queryString = !!applicationId ? `?context=${context}`.concat('&appId=', applicationId) : `?context=${context}`;

    const req = this._http.get(FilterService.baseUrl + queryString)
      .map(data => {
        if (data.body.hasOwnProperty('filterDefinitions')) {
          get(data, 'body.filterDefinitions').forEach(filterDef => this.updateFilterDefinitionsStore(filterDef));
          const forms = get(data, 'body.forms');
          if (forms) {
            forms.forEach(form => this._formHandler.addForm(form.id, form.inputs, form.reflects, form.dataBinding, form.configBindings, form, true));
          }
          return data.body['filters'];
        }
        return data.body;
      })
      .publish();
    req.connect();
    return req;
  }

  public createFilter(filterName: string, type: string, subType: string, query: string, context: any, createDefaultFilter: boolean = false, formData?: any): Observable<any> {
    const additionalContextParams: any = { subType: subType};
    const params: any = {
      filter: {
        name: filterName,
        userId: this.authService.user.id,
        objectName: type,
        query: query,
        clientDate: this.datePipe.transform(new Date(), 'dd MMM yyyy')
      },
      additionalContextParams: additionalContextParams
    };

    if (createDefaultFilter) {
      params.additionalContextParams['default'] = true;
    }

    if (formData) {
      params.filter.formData = formData;

      if (this._filterByOrgContext) {
        params.additionalContextParams['organization'] = this.authService.user.organization;
      }
    }

    if (context.hasOwnProperty('filterDefinitionId')) {
      params.filter.filterDefinitionId = context['filterDefinitionId'];
    } else if (context.hasOwnProperty('testTypeId')) {
      params.filter.testTypeId = context['testTypeId'];
      params.filter.aberrationType = subType;
    }

    if (context.hasOwnProperty('subTestId')) {
      params.additionalContextParams['testTypeId'] = context['subTestId'];
    }

    return this._http.post(FilterService.baseUrl + '/', params)
      .pipe(
        map(data => data.body),
        catchError((err) =>
        {
          console.error('filter Service : createFilter() : url : ',FilterService.baseUrl,' : Error : ', err.status,'-', err.message);
          return throwError(err)})
      );
  }

  public updateManagedFilter(filterObj: Filter): Observable<any> {
    if (this.isDefaultFilter(filterObj.name)) {
      this.filterAC.getState([FILTER_CURRENT])
        .pipe(
          take(1)
        )
        .subscribe((current: string) => {
          current = cloneDeep(current);
          this.initSelectedFilterPropsWithSelectedFilterValues(current, filterObj);
          this.notificationMessageService.error(this.notificationMessage.DEFAULT_FILTER_CANNOT_BE_CHANGED);

          return Observable.empty();
        });
    } else {
      return this.updateFilter(filterObj);
    }
  }

  public updateFilter(filterObj: Filter): Observable<any> {
    return this._http.put(FilterService.baseUrl + '/' + filterObj.id, { filter: filterObj})
      .pipe(
        map(data => data.json()),
        map(item => item),
        catchError(err => {
          console.error('filter Service : updateFilter() : url : ',FilterService.baseUrl, ' : Error : ', err.status,'-', err.message);
          return throwError(err)})
      );
  }

  public deleteFilter(filterId: string): any {
    const params: any = {
      filterId: filterId
    };

    return this._http.delete(FilterService.baseUrl + '/' + filterId, params)
      .pipe(
        map(item => item),
        catchError(err =>{
          console.error('filter Service : deleteFilter() : url : ',FilterService.baseUrl, ' : Error : ', err.status,'-', err.message);
          return throwError(err)})
      );
  }

  // Replaces the loadSearchableFields in filterPanel.js - revisionId replaces the statisticsId
  // objectName can be "aberration", "currationInfo" etc.
  public getCategories(objectName: string, additionalParams?: any) {
    const params: any = {
      objectName: objectName
    };

    if (additionalParams) { // testId , statisticsId, filterDefinitionId (when there are no statistics)
      Object.keys(additionalParams).forEach(key => params[key] = additionalParams[key]);
    }

    if (this._categoriesObservable && objectName === 'aberration') {
      return this._categoriesObservable;
    } else {
      this._categoriesObservable = this._http.get(FilterService.baseUrl + '/getDisplaySearchableFields', params)
        .pipe(
          map(data => data.json()),
          map((item: FilterCategory) => item),
          share()
        )
        .catch(err => {
          return Observable.of(err.error);
        });

      return this._categoriesObservable;
    }
  }

  public initFilterProperties(categories: any): void {
    const categoryNames = Object.keys(categories);
    if (categoryNames) {
      categoryNames.forEach(categoryName => {
        categories[categoryName].filters.forEach(filterProperty => {
          if (!filterProperty.selectedValue) {
            filterProperty.selectedValue = { };
            this.removeFilterValue(filterProperty);
            this.removeStatisticsValue(filterProperty);
          }
        });
      });
    }
  }

  public clearAppliedFilters(filterPropertyToClear?: any): void {
    let filterProps: any[] = [];
    let currentTypeAndSubtype: string = '';

    this.filterAC.getAppliedFilterProperties().pipe(
      take(1),
      map((filterPropertiesRes: any[]) => {
        if (filterPropertyToClear && filterPropertiesRes && filterPropertiesRes.length > 0) {
          filterProps = filterPropertiesRes.filter(filterProp => filterProp.name !== filterPropertyToClear.name);
        }
      }),
      flatMap(() => {
        return this.filterAC.getState([FILTER_CURRENT])
          .take(1);
      }),
      map((current: string) => {
        currentTypeAndSubtype = current;
      }),
      flatMap(() => {
        return this.filterAC.getCurrentSelectedFilter();
      })
    )
      .subscribe((currentSelected) => {
        // Fix of GEN-159287 moves to side effect  - GEN-160740 Can't clean filter panel effects content
        // currentSelected = cloneDeep(currentSelected);
        if (currentSelected && currentSelected.categories) {
          if (filterPropertyToClear) {
            this.clearFilterProperties(currentSelected.categories, [filterPropertyToClear]);
          }
          else {
            this.clearFilterProperties(currentSelected.categories);
          }
          currentSelected.filterTypeAndSubtype = currentTypeAndSubtype;
          this.filterAC.setCurrentSelectedFilter(currentSelected);

          if (currentTypeAndSubtype.indexOf('_') !== -1) {
            const filterTypes = FilterUtils.getFilterTypes(currentTypeAndSubtype);
            this.filterAC.setAppliedFilterProperties(filterProps, currentTypeAndSubtype);
            this.applyFilter(filterTypes[0], filterTypes[1], currentSelected.categories, currentSelected.filter.formData);
          }
        }
      });
  }

  public clearFilterPropertiesInStore(): Observable<{ filterTypeAndSubtype: string, filter: Filter, categories: any }> {
    return this.getCurrentSelectedFilterFromStore()
      .take(1)
      .map(currentSelectedFilter => {
        if (currentSelectedFilter) {
          if (currentSelectedFilter.filter && currentSelectedFilter.categories) {
            this.clearFilterProperties(currentSelectedFilter.categories);
          }
        }

        return currentSelectedFilter;
      });
  }

  private clearFilterProperties(categories: any, filterPropsToClear?: any[]): any[] {
    const clearedFilterProps: any[] = [];

    const categoryNames = Object.keys(categories);
    if (!filterPropsToClear) {
      categoryNames.forEach(categoryName => {
        categories[categoryName].filters.forEach(filterProperty => {
          if (filterProperty.selectedValue) {
            if (filterProperty.config) {
              this.removeFilterValue(filterProperty);
              this.removeStatisticsValue(filterProperty);
            }
            else {
              this.removeFormFilterValue(filterProperty);
            }
            clearedFilterProps.push(filterProperty);
          }
        });
      });
    } else {
      filterPropsToClear.forEach(filterPropToClear => {
        categoryNames.forEach(categoryName => {
          categories[categoryName].filters.forEach(filterProperty => {
            if (filterProperty.name === filterPropToClear.name) {
              if (filterProperty.selectedValue) {
                this.removeFilterValue(filterProperty);
                this.removeStatisticsValue(filterProperty);
                clearedFilterProps.push(filterProperty);
              }
            }
          });
        });
      });
    }
    return clearedFilterProps;
  }

  public initSelectedFilterPropsWithSelectedFilterValues(filterTypeAndSubtype: string, selectedFilter: any, changeDetector?: ChangeDetectorRef): void {
    const selectedFilterValues = this.buildFiltersValuesMap(selectedFilter);
    this.updateSelectedFilterPropsValues(filterTypeAndSubtype, selectedFilter, selectedFilterValues, changeDetector);
  }

  public initAppliedFilters(categories: Categories, selectedFiltersValues: any, isFormFilter= false): any[] {
    const appliedFilterProperties: any[] = [];

    if (categories) {
      this.clearFilterProperties(categories);
      Object.keys(categories).forEach(categoryKey => {
        const filtersInCategory = categories[categoryKey].filters;
        filtersInCategory.forEach(filterProperty => {
          filterProperty.translatedName = this.getFilterLabel(filterProperty);
          if (selectedFiltersValues && selectedFiltersValues[0] && selectedFiltersValues[0].selectedFilterValues
            && selectedFiltersValues[0].selectedFilterValues[filterProperty.name]) {
            if (isFormFilter) {
                this.updateSelectedFormFilterValue(filterProperty, selectedFiltersValues[0].selectedFilterValues[filterProperty.name]);
              }
            else {
              this.updateSelectedFilterValue(filterProperty, selectedFiltersValues[0].selectedFilterValues[filterProperty.name]);
              this.updateSelectedStatisticsValue(filterProperty, selectedFiltersValues[0].selectedFilterValues[filterProperty.name]);
            }
            if (selectedFiltersValues[0].priority) {
              if (selectedFiltersValues[0].priority === 1) {
                appliedFilterProperties.push({ ...filterProperty});
              }
            } else {
              appliedFilterProperties.push({ ...filterProperty});
            }
          }
        });
      });
    }

    return appliedFilterProperties;
  }

  private getFilterLabel(filterItem: FilterProperty): string {
    // Getting the filter title from the relevant worklist column title
    const column = this.currentWorklistColumns.find( _column =>
      _column['key'] === filterItem.name || ((filterItem as any)['alias'] && (filterItem as any)['alias'].includes(_column['key']))
    );

    if (column) {
      return column['label'];
    }
    // If worklist column was not found, the title will be the filter name
    return this.translateService.instant('filter.' + filterItem.name, filterItem.name);
  }

  public getFiltersFromStore(): Observable<Map<string, Filter>> {
    let filters: Observable<Map<string, Filter>> = this.filterAC.getState([FILTER_CURRENT])
      .take(1)
      .flatMap(current => {
        return this.filterAC.getCurrentAppliedFilters(current);
      })
      .map(currentFilters => {
        if (currentFilters.data) {
          filters = currentFilters.data;
        }
        return filters;
      });
    return filters;
  }

  // Revives selectedFilterPropertiesValues
  public buildFilterString(filterQueryObject: any): string {
    let filterQueryString = '', delimiter;
    if (filterQueryObject) {
      const selectedFilterPropertyKeys = Object.keys(filterQueryObject);
      selectedFilterPropertyKeys.forEach(filterPropertyKey => {
        if (filterQueryObject.hasOwnProperty(filterPropertyKey)) {
          delimiter = ['>=', '<=', '='].includes(filterQueryObject[filterPropertyKey]) ? '' : '=';
          filterQueryString += `&${filterPropertyKey}${delimiter}${filterQueryObject[filterPropertyKey]}`;
        }
      });
    }
    return filterQueryString;
  }

  public buildFiltersValuesMap(selectedFilters: any): any[] {
    if (!selectedFilters) {
      return [];
    }
    else if (!(selectedFilters instanceof Array)) {
      selectedFilters = [selectedFilters];
    }
    const filterValuesArray: any[] = [];

    selectedFilters.forEach(selectedFilter => {
      let filterSubtype: any = { };
      const selectedFilterValues: any = { };
      if (selectedFilter) {
        filterSubtype = selectedFilter.filterSubtype || { };
        if (selectedFilter.context) {
          filterSubtype = selectedFilter.context.find(reference => {
            return reference.resourceType == 'subType';
          });
          if (!filterSubtype) {
            filterSubtype = selectedFilter.context.find(reference => {
              return reference.resourceType == 'aberrationType';
            });
          }
        }
        if (selectedFilter.query) {
          const selectedFilterData = selectedFilter.query.split('&');
          selectedFilterData
            .filter(filterTmp => filterTmp !== '')
            .forEach(filterData => {
              const filterName = filterData.substr(0, filterData.indexOf('='));
              selectedFilterValues[filterName] = filterData.substr(filterData.indexOf('=') + 1);
            });
        }
        if (filterSubtype) {
          const filterTypeForArray = filterSubtype.reference ? filterSubtype.reference : filterSubtype;
          filterValuesArray.push({
            filterId: selectedFilter.id,
            selectedFilterValues: selectedFilterValues,
            filterType: filterTypeForArray,
            default: selectedFilter.default,
            priority: selectedFilter.priority
          });
        }
      }
    });

    return filterValuesArray;
  }

  private getFilterValue(filterPropertyValue: any): string {
    if (filterPropertyValue.value && filterPropertyValue.value !== '') {
      return this.buildMultiselectValue(filterPropertyValue);
    }
    return this.buildRangeValue(filterPropertyValue);
  }

  private getFilterValueByProperty(filterProperty: any): string {
    if (filterProperty.type.toLowerCase() === 'datepicker' && filterProperty.selectionMode && filterProperty.selectionMode.toLowerCase() === 'range'
      && filterProperty.selectedValue.value instanceof Array && filterProperty.selectedValue.value.length > 1) {
      return this.createDateRangeExpression(filterProperty.name, filterProperty.selectedValue.value[0].id, filterProperty.selectedValue.value[1].id);
    }
    return this.getFilterValue(filterProperty.selectedValue);
  }

  private buildMultiselectValue(filterPropertyValue: any): string {
    let value = '';
    filterPropertyValue.value.forEach(item => {
      value = value + item.id + ',';
    });
    value = value.slice(0, -1);
    return value;
  }

  private buildRangeValue(filterPropertyValue: any): string {
    const fromPrefix = '>=';
    const toPrefix = '<=';
    let value: string;

    const fromValue: string = filterPropertyValue.from;
    const toValue: string = filterPropertyValue.to;
    if (fromValue && fromValue !== '') {
      value = fromPrefix + fromValue;
    }
    if (toValue && toValue !== '') {
      value = value ? '[' + value + ',' + toPrefix + toValue + ']' : toPrefix + toValue;
    }
    return value;
  }

  public updateSelectedFilterPropsValues(filterTypeAndSubtype: string, selectedFilter: Filter, selectedFiltersValues: any, changeDetector?: ChangeDetectorRef): void {
    this.updateSavedFilterPropsValues(filterTypeAndSubtype, selectedFilter, selectedFiltersValues, changeDetector)
      .subscribe((currentSelectedFilterAndProperties: any) => {
        currentSelectedFilterAndProperties = cloneDeep(currentSelectedFilterAndProperties); // cloneDeep is needed here! Without it wrong current quick filter query might be sent when existing fast from a test
        this.setCurrentSelectedFilter(currentSelectedFilterAndProperties.currentSelectedFilter);
        this.filterAC.setAppliedFilterProperties(currentSelectedFilterAndProperties.appliedFilterProperties, filterTypeAndSubtype);
        if (changeDetector) {
          changeDetector.markForCheck();
        }
      });
  }

  public updateSavedFilterPropsValues(filterTypeAndSubtype: string, selectedFilter: Filter, selectedFiltersValues: any, changeDetector?: ChangeDetectorRef): Observable<any> {
    let categoryInit;
    const appliedFilterProperties: any[] = [];

    return this.getCurrentSelectedFilterFromStore()
      .take(1)
      .filter(data => !!data)
      .flatMap((currentSelectedFilter) => {
        // TODO Df integration goes here
        return this.getFormFilter().pipe(take(1)).map(formFilter => {
          const isForm = formFilter.isForm;

          if (currentSelectedFilter && currentSelectedFilter.filter) {
            if (currentSelectedFilter.categories) {
              this.clearFilterProperties(currentSelectedFilter.categories);
              Object.keys(currentSelectedFilter.categories).forEach(categoryKey => {
                categoryInit = currentSelectedFilter.categories[categoryKey].hasOwnProperty('isCollapsed');
                currentSelectedFilter.categories[categoryKey].isCollapsed = categoryInit ? currentSelectedFilter.categories[categoryKey].isCollapsed : true;
                const filtersInCategory = currentSelectedFilter.categories[categoryKey].filters;
                filtersInCategory.forEach(filter => {
                  filter.translatedName = this.getFilterLabel(filter);
                  if (selectedFiltersValues && selectedFiltersValues[0] && selectedFiltersValues[0].selectedFilterValues
                    && selectedFiltersValues[0].selectedFilterValues[filter.name]) {

                    if (isForm) {
                      this.updateSelectedFormFilterValue(filter, selectedFiltersValues[0].selectedFilterValues[filter.name]);
                    }

                    else {
                      this.updateSelectedFilterValue(filter, selectedFiltersValues[0].selectedFilterValues[filter.name]);
                      this.updateSelectedStatisticsValue(filter, selectedFiltersValues[0].selectedFilterValues[filter.name]);
                    }
                    appliedFilterProperties.push({ ...filter});
                    currentSelectedFilter.categories[categoryKey].isCollapsed = categoryInit ? currentSelectedFilter.categories[categoryKey].isCollapsed : false;
                  }
                  if (!isForm) {
                    this.updateFilterValue(filter);
                  }
                });
              });
            }
            currentSelectedFilter.filter = selectedFilter;
          }

          return { currentSelectedFilter: currentSelectedFilter, appliedFilterProperties: appliedFilterProperties};
        });
      });
  }

  public setCurrentSelectedFilter(filter: any): void {
    this.filterAC.setCurrentSelectedFilter(filter);
  }

  public setAppliedFilterProperties(appliedFilterProperties: any[], currentFilterTypeAndSubtype?: string): void {
    const appliedFilterPropertiesWithSelectedValues = appliedFilterProperties
      .filter(filterProperty => {
        return filterProperty.hasOwnProperty('selectedValue')
          && this.isFilterPropertyApplied(filterProperty);
      });
    this.filterAC.setAppliedFilterProperties(appliedFilterPropertiesWithSelectedValues, currentFilterTypeAndSubtype);
  }

  private updateFilterValue(filterProperty: FilterProperty): void {
    const filterType = get(filterProperty, 'config.filterType');
    if (filterType === 'checkboxlist-icon' && filterProperty.statistics) {
      filterProperty.config.values = filterProperty.statistics.values;
    }
  }

  private updateSelectedFilterValue(filter: any, newValue: any) {
    let selectedValues;
    switch (filter.config.filterType) {
      case 'range':
        const findDigitRegExpr = /[\d+(\.\d+)]+/;
        const from = newValue.match(/>=[\d+(\.\d+)]+/);
        const to = newValue.match(/<=[\d+(\.\d+)]+/);
        filter.selectedValue.from = from ? Number(from.toString().match(findDigitRegExpr)) : from;
        filter.selectedValue.to = to ? Number(to.toString().match(findDigitRegExpr)) : to;
        break;
      case 'dropdown':
        if (!filter.config.values) { return; }
        selectedValues = newValue.split(',');
        const filterValueIds = new Set();
        selectedValues.forEach(item => {
          const itemIsNull: boolean = (item == 'null');
          const isItemExistsInCurrentFilter = filter.config.values.find(val => {
            if (!val.id) {
              return (val.id == null && itemIsNull) ? true : null;
            }
            let splitValues = [];
            if (typeof val.id === 'string' || val.id instanceof String) {
               splitValues = val.id.split(',');
            }
            else if (typeof val.id === 'number') {
              splitValues = [val.id];
 }
            else  if (val.id instanceof Array) {
              splitValues = [...val.id];
  }
            return splitValues.some(element => {
              return element.toString() === item || (element == null && itemIsNull);
            });
          });
          if (isItemExistsInCurrentFilter && !filterValueIds.has(isItemExistsInCurrentFilter.id)) {
            filter.selectedValue.value.push({
              id: isItemExistsInCurrentFilter.id == 'null' ? null : isItemExistsInCurrentFilter.id,
              label: isItemExistsInCurrentFilter.label
            });
            filterValueIds.add(isItemExistsInCurrentFilter.id);
          }
        });
        break;
      case 'autocomplete':
        selectedValues = newValue.split(',');
        selectedValues.forEach(item => {
          const isNotDuplicateItem = (filter.selectedValue.value.findIndex(x => x.id === item) === -1);
          if (isNotDuplicateItem) {
            filter.selectedValue.value.push({ id: item});
          }
        });
        break;
      case 'checkboxlist-icon':
        if (!filter.config.values) { return; }
        selectedValues = newValue.split(',');
        selectedValues.forEach(item => {
          const isItemExistsInCurrentFilter = filter.config.values.find(val => {
            return val.id === item;
          });
          if (isItemExistsInCurrentFilter) {
            isItemExistsInCurrentFilter.isSelected = true;
            filter.selectedValue.value.push({ id: item, label: item});
          }
        });
        selectedValues.forEach(item => {
          filter.selectedValue.value.push({ id: item});
        });
        break;
      default:
        filter.selectedValue.value = newValue;
        break;
    }
  }

  private updateSelectedStatisticsValue(filterProperty: FilterProperty, newStatValue: string): void {
    switch (filterProperty.config.statsType) {
      case 'checkboxlist':
        // if (!filterProperty.statistics.values) {
        //     return;
        // }

        const selectedValues = newStatValue.split(',');
        selectedValues.forEach(item => {
          let isItemExistsInCurrentFilter;
          if (filterProperty.statistics && filterProperty.statistics.values) {
            isItemExistsInCurrentFilter = filterProperty.statistics.values.find(val => {
              const formalizedValue = val.value !== undefined ? val.value : 'null';
              return formalizedValue === item;
            });
          } else {
            isItemExistsInCurrentFilter = { };
            isItemExistsInCurrentFilter.text = item;
            isItemExistsInCurrentFilter.value = item;
          }
          if (isItemExistsInCurrentFilter) {
            isItemExistsInCurrentFilter.isSelected = true;
          }
        });
        break;
    }
  }

  // handling DF form Filter
  getFormFilter() {
    return this.filterAC.getState([FILTER_CURRENT]).switchMap((current: string) => {
      return this.filterAC.getState(['formFilter']).take(1).map(data => {
        if (data.has(current)) {
          return data.get(current);
        } else {
          return { };
        }
      });
    });
  }

  private removeFilterValue(filterProperty: FilterProperty): void {
    if (filterProperty.type && !filterProperty.config) {
      this.removeFormFilterValue(filterProperty);
    }
    else {
    switch (filterProperty.config.filterType) {
      case 'range':
        filterProperty.selectedValue.from = null;
        filterProperty.selectedValue.to = null;
        break;
      case 'dropdown':
      case 'autocomplete':
        filterProperty.selectedValue.value = [];
        break;

      case 'multiselect_with_manual':
      case 'multiselect':
        filterProperty.selectedValue.value = [];
        break;
      case 'checkboxlist-icon':
        filterProperty.selectedValue.value = [];
        if (filterProperty.config.values) {
          filterProperty.config.values.forEach(value => {
            if (value.isSelected) {
              value.isSelected = false;
            }
          });
        }
        break;
      default:
        filterProperty.selectedValue.value = null;
        break;
    }
    }
  }

  // DF Integration
  createCategories(formConfig: any, formData: any) {
    const updatedCategory = { filters: []};
    formConfig.map(inputConfigs => {
      const transformTemplate = this._searchService.getTransformObject(inputConfigs.options.resource);
      const filterCategory: any = { ...inputConfigs, selectedValue: { }};
      if (!inputConfigs['noSaveData']) {
        switch (inputConfigs.type.toLocaleUpperCase()) {
          case 'DROPDOWN':
          case 'SELECT':
          case 'AUTOCOMPLETE':
            filterCategory.selectedValue.value = [];
            if (formData[inputConfigs.name]) {
              // Using 'pathToSourceValue' when need to selected a different value (e.g. name instead of code).
              const alterntivePath: string = inputConfigs.pathToField ? inputConfigs.pathToField : inputConfigs.pathToSourceValue;
              const filterValue = get(formData[inputConfigs.name], `${alterntivePath ? alterntivePath : get(transformTemplate, 'code')}`);
              filterCategory.selectedValue.value.push({ id: filterValue ? filterValue : formData[inputConfigs.name]});
            }
            break;
          case 'MULTISELECT_WITH_MANUAL':
          case 'MULTISELECT':
            filterCategory.selectedValue.value = [];
            if (formData[inputConfigs.name]) {
              formData[inputConfigs.name].map(data => {
                const filterValue = get(data, `${inputConfigs.pathToField ? inputConfigs.pathToField : get(transformTemplate, 'code')}`);
                filterCategory.selectedValue.value.push({ id: filterValue ? filterValue : data.code });
              });
            }
            break;
          case 'DATEPICKER':
            filterCategory.selectedValue.value = [];
            const today = new Date();
            const tempValue = [];
            if (formData) {
              if (Array.isArray(formData[inputConfigs.name])) {
                formData[inputConfigs.name].map(data => {
                  filterCategory.selectedValue.value.push({ id: data});
                });
              } else if (formData[inputConfigs.name] && formData[inputConfigs.name].toLowerCase() === FILTER_DATE_NOW) {
                tempValue.push(new Date(today));
                filterCategory.selectedValue.value.push({ id: tempValue[0].toPlatformFormat()});

              } else if (formData[inputConfigs.name] && formData[inputConfigs.name].toLowerCase() === FILTER_DATE_NEXT_WEEK) {
                tempValue.push(new Date(today));
                filterCategory.selectedValue.value.push({ id: tempValue[0].toPlatformFormat()});
                tempValue.push(new Date(today.setDate(today.getDate() + 6)));
                filterCategory.selectedValue.value.push({ id: tempValue[1].toPlatformFormat()});

              }
              else if (formData[inputConfigs.name] && formData[inputConfigs.name].toLowerCase() === FILTER_DATE_COMING_WEEK) {
                // TODO - Previous date selection
                tempValue.push(new Date(2018, 10, 1));
                filterCategory.selectedValue.value.push({ id: tempValue[0].toPlatformFormat()});
                tempValue.push(new Date(today.setDate(today.getDate() + 6)));
                filterCategory.selectedValue.value.push({ id: tempValue[1].toPlatformFormat()});

              }
              else if (formData[inputConfigs.name] && formData[inputConfigs.name].toLowerCase() === FILTER_DATE_LAST_WEEK) {
                tempValue.push(new Date(today.setDate(today.getDate() - 6)));
                filterCategory.selectedValue.value.push({ id: tempValue[0].toPlatformFormat()});
                tempValue.push(new Date(today.setDate(today.getDate())));
                filterCategory.selectedValue.value.push({ id: tempValue[1].toPlatformFormat()});
              }
              else if (formData[inputConfigs.name] && (!inputConfigs.selectionMode || inputConfigs.selectionMode.toLowerCase() != 'range')) { // Handles date filter with single date selection (not range)
                filterCategory.selectedValue.value.push({ id: formData[inputConfigs.name]});
              }
            }
            else {
              filterCategory.selectedValue.value.push({ id: formData[inputConfigs.name]});
            }
            break;
          case 'NUMBER':
            if (typeof formData[inputConfigs.name] === 'number') {
              filterCategory.selectedValue.value = [];
              filterCategory.selectedValue.value.push({ id: formData[inputConfigs.name]});
            }
            else {
              filterCategory.selectedValue.value = null;
            }
            break;
          default:
            if (formData[inputConfigs.name]) {
              filterCategory.selectedValue.value = [];
              filterCategory.selectedValue.value.push({ id: formData[inputConfigs.name]});
            } else {
              filterCategory.selectedValue.value = null;
            }
            break;
        }
      }
      updatedCategory.filters.push(filterCategory);
    });
   return updatedCategory ;
  }

  private updateSelectedFormFilterValue(filter: any, newValue: any) {

    switch (filter.type.toLocaleUpperCase()) {
      case 'DROPDOWN':
      case 'SELECT':
      case 'AUTOCOMPLETE':
        if (typeof (newValue) === 'string') {
          filter.selectedValue.value.push({ id: newValue});
        }
        else {
          filter.selectedValue.value.push({ id: newValue.code});
        }
        break;
      case 'MULTISELECT_WITH_MANUAL':
      case 'MULTISELECT':
        filter.selectedValue.value = [];
        newValue = (typeof (newValue) === 'string') ? newValue.split(',') : newValue;
        newValue.map(data => {
          filter.selectedValue.value.push({ id: data});
        });
        break;
      case 'DATEPICKER':
        filter.selectedValue.value = [];
        if (typeof newValue === 'string') {
          const today = new Date();
          const tempValue = [];
          if (newValue.toLowerCase() === FILTER_DATE_NEXT_WEEK) {
            tempValue.push(new Date(today));
            tempValue.push(new Date(today.setDate(today.getDate() + 6)));
            newValue = `${tempValue[0].toPlatformFormat()},${tempValue[1].toPlatformFormat()}`;
          }
          if (newValue.toLowerCase() === FILTER_DATE_COMING_WEEK) {
            // TODO - Previous date selection
            tempValue.push(new Date(2018, 10, 1));
            tempValue.push(new Date(today.setDate(today.getDate() + 6)));
            newValue = `${tempValue[0].toPlatformFormat()},${tempValue[1].toPlatformFormat()}`;
          }
          if (newValue.toLowerCase() === FILTER_DATE_NOW) {
            tempValue.push(new Date(today));
            newValue = `${tempValue[0].toPlatformFormat()}`;
          }
          if (newValue.toLowerCase() === FILTER_DATE_LAST_WEEK) {
            tempValue.push(new Date(today.setDate(today.getDate() - 6)));
            tempValue.push(new Date(today));
            newValue = `${tempValue[0].toPlatformFormat()},${tempValue[1].toPlatformFormat()}`;
          }

          if (newValue.includes('@@')) {
            const newValueArrayValues = newValue.split('@@');
            if (Array.isArray(newValueArrayValues)) {
              newValueArrayValues.filter(filterTmp => filterTmp !== '').forEach(data => {
                const filterValue = data.substr(data.lastIndexOf('=') + 1);
                filter.selectedValue.value.push({ id: filterValue});
              });
            }
          } else {
            newValue = newValue.split(',');

            if (Array.isArray(newValue)) {
              newValue.map(data => {
                filter.selectedValue.value.push({ id: data});
              });
            } else {
              filter.selectedValue.value.push({ id: newValue});
            }
          }
        }
        break;
      default:
        filter.selectedValue.value = [];
        filter.selectedValue.value.push({ id: newValue});
        break;
    }
  }

  private removeFormFilterValue(inputConfigs: FilterProperty): void {
    switch (inputConfigs.type.toLocaleUpperCase()) {
      case 'DROPDOWN':
      case 'SELECT':
      case 'AUTOCOMPLETE':
      case 'MULTISELECT':
      case 'MULTISELECT_WITH_MANUAL':
        inputConfigs.selectedValue.value = [];
        break;
      default:
        inputConfigs.selectedValue.value = null;
        break;
    }
  }

  // Adding Bundle search to get Filter defination
  getFilterDefinition(filterDefinitionId): Observable<any> {
    if (!filterDefinitionId) {
      return of({ });
    } else {
        return this._bundle.searchCall({
            queries: [
                {
                    targetResource: 'FilterDefinition',
                    queryString: `_id=${filterDefinitionId}`
                }
            ]
        });
    }
  }

  fetchFormConfig(formId: string) {
     return this._formHandler.getForm(formId).
        pipe(
          filter(data => !!data),
          map((data: any) => {
            return this.createCategories(data.inputs, { });
        }));
  }

  fetchFormInput(formId: string) {
    return this._formHandler.getForm(formId).pipe(filter(data => !!data));
  }

  // Df Integration Ends here
  private removeStatisticsValue(filterProperty: FilterProperty): void {
    if (!filterProperty.statisticsSelectedValue) {
      filterProperty.statisticsSelectedValue = { };
    }
    // TODO Add filter Form related changes
    if (!filterProperty.type && filterProperty.config.statsType == 'checkboxlist') {
      if (filterProperty.statistics && filterProperty.statistics.values) {
        filterProperty.statistics.values.forEach(value => {
          if (value.isSelected) {
            value.isSelected = false;
          }
        });
      }
    }
  }

  public isDefaultFilter(newFilterName: string): boolean {
    let isDefaultFilter: boolean = false;

    this.getFiltersFromStore()
      .subscribe(res => {
        res = cloneDeep(res);
        const defaultFilters = Array.from(res.values()).filter(filter => (filter.default === true && (filter.name).toLowerCase().trim() === newFilterName.toLowerCase().trim()));
        if (defaultFilters && defaultFilters.length > 0) {
          isDefaultFilter = true;
        }
      });

    return isDefaultFilter;
  }

  public filterNameExists(newFilterName: string): any {
    let filter: any = { };

    const filterName$: Subscription = this.getFiltersFromStore()
      .subscribe(res => {
        res = cloneDeep(res);
        const filters = Array.from(res.values()).filter(filter => (filter.name).toLowerCase().trim() == newFilterName.toLowerCase().trim());
        if (filters && filters.length > 0) {
          filter = filters[0];
        }
      });

    if (filterName$) { filterName$.unsubscribe(); }

    return filter;
  }

  public changeSelectedFilter(item: any, type, subtype, changeDetector?: ChangeDetectorRef) {
    let selectedFilter: Filter;
    if (item) {
        let defaultFilter: Filter;
        this.getDefaultFilter(type, subtype)
          .pipe(map(defaultFilterRes => {
            defaultFilterRes = cloneDeep(defaultFilterRes);
            defaultFilter = defaultFilterRes;
          }))
          .subscribe();

        if (defaultFilter.id !== item) {
            this.getManagedFiltersFromStore()
                .subscribe(managedFilters => {
                  managedFilters = cloneDeep(managedFilters);
                    const matchingManagedFilters =
                    managedFilters.filter(managedFilter => managedFilter.id == item);
                    if (matchingManagedFilters && matchingManagedFilters.length > 0) {
                        selectedFilter = matchingManagedFilters[0];
                    }
                });
        } else {
            selectedFilter = defaultFilter;
        }

        this.filterAC.getState([FILTER_CURRENT])
            .take(1)
            .subscribe((currentFilterTypeAndSubtype: string) => {
              currentFilterTypeAndSubtype = cloneDeep(currentFilterTypeAndSubtype);
                this.initSelectedFilterPropsWithSelectedFilterValues(currentFilterTypeAndSubtype, selectedFilter, changeDetector);
                this.applySelectedFilter(type, subtype);
                changeDetector.markForCheck();
            });
      }
  }

  private applySelectedFilter(type, subtype): void {
    let selectedFilterCategories: any;
    let selectedFilterFormData: any;
    this.filterAC.getCurrentSelectedFilter(type + '_' + subtype)
        .subscribe((currentFilterAndCategories) => {
          currentFilterAndCategories = cloneDeep(currentFilterAndCategories);
          selectedFilterCategories = currentFilterAndCategories.categories;
          selectedFilterFormData = currentFilterAndCategories.filter.formData;
        });
    this.applyFilter(type, subtype, selectedFilterCategories, selectedFilterFormData);
  }

  public resolveFilterDefinitionId(filterDefId: string) {
    if (!filterDefId) {
      return of({ });
    }

    return this._configurationStoreHandler.getConfigById('FilterDefinition', filterDefId)
      .pipe(
        tap(filterDef => {
          if (!(filterDef)) {
            this.getFilterDefinition(filterDefId).subscribe((response: any) => {
              response = cloneDeep(response);
              if (response) {
               if (has(response, '[0].response')) {
                 this._configurationStoreHandler.setConfiguration(get(response, '[0].response.entry.0.resource', { }));
               }
              }
            });
          }
        }),
        filter(config => config != null),
        take(1)
      );
  }

  private updateFilterDefinitionsStore(resource) {
    this._configurationStoreHandler.setConfiguration(resource);
  }

  private createDateRangeExpression(param: string, from, to): string {
    // user thinks that filter time is his local time, so convert it to UTC, because data is stored in DB in UTC/GMT format
    if (typeof from  === 'object' && typeof to  === 'object' ) {
              const fromDateWithTimeStamp = from.toPlatformFormat() + '  00:00:00';
              const toDateWithTimeStamp = to.toPlatformFormat() + ' 23:59:59';
              return '>=' + moment(fromDateWithTimeStamp).utc().format('YYYY-MM-DDTHH:mm:ss') + '@@' + param + '=<=' + moment(toDateWithTimeStamp).utc().format('YYYY-MM-DDTHH:mm:ss');
              } else if ( typeof from === 'string' && from.includes('T') && to.includes('T')) {
                return '>=' + from + '@@' + param + '=<=' + to;
              } else {
                const fromLocalTime = new Date(from + ' 00:00:00');
                const toLocalTime = new Date(to + ' 23:59:59');
                return '>=' + moment(fromLocalTime).utc().format('YYYY-MM-DDTHH:mm:ss') + '@@' + param + '=<=' + moment(toLocalTime).utc().format('YYYY-MM-DDTHH:mm:ss');
              }
  }

  private checkExcludedCategoryFields(definition, categoriesRes): void {
    Object.keys(categoriesRes).forEach(key => {
      const filterData = definition.find(item => item.id === key);
      if (!!filterData && has(filterData, 'excludedProperties')) {
        let excludedFilter;
        categoriesRes[key].filters.forEach(filter => {
          if (get(filterData, 'excludedProperties', []).indexOf(filter.name) >= 0) {
            excludedFilter = filter;
          }
        });
        if (!!excludedFilter) {
          categoriesRes[key].filters.remove(excludedFilter);
        }
      }
    });
  }

  public filterChanged() {
    this._lastFilterPanelValues = {selectedFilterId: '', selectedFilterCategories: null, formData: null};
  }

  public clearAberrationObservable() {
    this._categoriesObservable = null;
  }

  public setSelectedRows(value: any[]) {
    this._selectedRows$.next(value);
  }

  public getSelectedRows(): BehaviorSubject<any[]> {
    return this._selectedRows$;
  }
}
